<template>
  <div class="seo-page xbox-game-pass-update">
    <section class="seo-hero">
      <span class="seo-badge">Subscription Update</span>
      <h1>Xbox Game Pass Renames Its Plans and Raises Prices</h1>
      <p>
        Microsoft raised Xbox Game Pass pricing while reshuffling plan names to Essential, Premium, and Ultimate.
        As of October 2, 2025, Game Pass Ultimate jumps 50 percent to $29.99 per month and the tiers broaden access to PC
        and cloud libraries.
      </p>
      <div class="seo-cta-group">
        <router-link class="seo-primary-btn" to="/">Launch steamplayti.me</router-link>
        <a class="seo-secondary-link" href="#plan-changes">See plan changes →</a>
      </div>
      <ul class="seo-highlight-list">
        <li v-for="highlight in highlights" :key="highlight.copy">
          <span class="seo-highlight-icon">{{ highlight.icon }}</span>
          <div>{{ highlight.copy }}</div>
        </li>
      </ul>
    </section>

    <section class="seo-section" id="plan-changes">
      <h2>How Each Game Pass Tier Is Changing</h2>
      <p>
        Microsoft is keeping three console-facing tiers, but the names and benefits are shifting. Existing members roll
        into the new setup automatically.
      </p>
      <div class="seo-grid">
        <article v-for="plan in planUpdates" :key="plan.newName" class="seo-card">
          <h3>{{ plan.newName }}</h3>
          <p><strong>Previously:</strong> {{ plan.oldName }}</p>
          <ul>
            <li v-for="point in plan.points" :key="point">{{ point }}</li>
          </ul>
        </article>
      </div>
    </section>

    <section class="seo-section">
      <h2>Expanded Libraries Land Immediately</h2>
      <p>
        Game Pass Essential gains a catalog of more than 50 games and now grants access to PC and cloud streaming. Premium
        mirrors those perks, and Microsoft says more than 40 additional titles arrive immediately, including Forza Horizon 5,
        Minecraft, Hogwarts Legacy, Frostpunk 2, and Diablo IV.
      </p>
      <div class="seo-grid">
        <div class="seo-card">
          <h3>New Additions to Premium & Essential</h3>
          <p>Microsoft highlights the following titles joining the library today:</p>
          <ul>
            <li v-for="game in headlineGames" :key="game">{{ game }}</li>
          </ul>
        </div>
        <div class="seo-card">
          <h3>Release Cadence</h3>
          <p>
            All Xbox Game Studios projects - with the exception of the Call of Duty series - are guaranteed to hit the
            service within a year of launch. Upcoming heavy hitters like Expedition 33 and DOOM: The Dark Ages are part of
            a pledge to deliver 75 day-one titles annually across the catalog.
          </p>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>PC Game Pass and Ultimate Perks Get a Boost</h2>
      <p>
        Ubisoft+ Classics is now bundled with PC Game Pass and Game Pass Ultimate, adding more than 40 Ubisoft releases to
        the subscription. PC players also retain first-day access to Xbox Game Studios games, Call of Duty included.
        Ultimate members keep every platform (console, PC, cloud) plus Ubisoft+ Classics, and they now receive Fortnite
        Crew perks such as monthly V-Bucks and cosmetic drops at no extra charge.
      </p>
    </section>

    <section class="seo-section">
      <h2>When the New Pricing Starts</h2>
      <p>
        Microsoft is staggering the rollout of the updated pricing. These are the milestone dates included in the
        announcement:
      </p>
      <div class="seo-grid">
        <div v-for="item in timeline" :key="item.label" class="seo-card">
          <h3>{{ item.label }}</h3>
          <p><strong>{{ item.date }}</strong></p>
          <p>{{ item.detail }}</p>
        </div>
      </div>
    </section>
    <SeoRelatedTools heading="Track Your Own Steam Playtime" />
  </div>
</template>

<script>
import { applyMeta, articleStructuredData } from '../utils/meta';
import SeoRelatedTools from '../components/SeoRelatedTools.vue';

const HIGHLIGHTS = [
  { icon: '💸', copy: 'Game Pass Ultimate rises from $19.99 to $29.99 per month - a 50% increase.' },
  { icon: '🆕', copy: 'Game Pass Core becomes Essential and Standard is now Premium, with automatic migrations.' },
  { icon: '☁️', copy: 'Essential and Premium now include PC and cloud play, while PC Game Pass folds in Ubisoft+ Classics.' },
];

const PLAN_UPDATES = [
  {
    newName: 'Game Pass Essential',
    oldName: 'Game Pass Core',
    points: [
      'Current Core members move over automatically with no action needed.',
      'Library expands to 50+ games spanning console, PC, and cloud.',
    ],
  },
  {
    newName: 'Game Pass Premium',
    oldName: 'Game Pass Standard',
    points: [
      'Subscribers are upgraded automatically under the new branding.',
      'Includes the expanded PC and cloud library.',
      'Microsoft says the monthly price stays put while the catalog grows.',
      'More than 40 additional games arrive immediately, led by Forza Horizon 5 and Minecraft.',
    ],
  },
  {
    newName: 'Game Pass Ultimate',
    oldName: 'Game Pass Ultimate',
    points: [
      'Monthly price jumps from $19.99 to $29.99, a 50% increase.',
      'Keeps console, PC, and cloud access bundled together.',
      'Adds Ubisoft+ Classics and Fortnite Crew membership with monthly rewards.',
    ],
  },
  {
    newName: 'PC Game Pass',
    oldName: 'PC Game Pass',
    points: [
      'Now includes Ubisoft+ Classics at no extra cost.',
      'Adds 40 more games to the PC catalog immediately.',
      'Continues day-one access to every Xbox Game Studios launch, Call of Duty included.',
    ],
  },
];

const HEADLINE_GAMES = [
  'Forza Horizon 5',
  'Minecraft',
  'Hogwarts Legacy',
  'Frostpunk 2',
  'Diablo IV',
];

const TIMELINE = [
  {
    label: 'October 1, 2025',
    date: 'New subscribers',
    detail: 'Microsoft applies the updated pricing and plan names to new memberships from this date.',
  },
  {
    label: 'November 4, 2025',
    date: 'Existing members',
    detail: 'Current subscribers transition to the higher pricing automatically.',
  },
];

const META = {
  title: 'Xbox Game Pass Price Increase and Plan Renames | steamplayti.me',
  description: 'Microsoft raises Xbox Game Pass prices, renames Core to Essential and Standard to Premium, and folds Ubisoft+ Classics into the library. See what changes and when.',
  keywords: 'Xbox Game Pass price increase, Game Pass Essential, Game Pass Premium, Game Pass Ultimate, Ubisoft+ Classics',
  canonical: 'https://steamplayti.me/blog/xbox-game-pass-price-update/',
  ogTitle: 'Xbox Game Pass Price Hike and Plan Renames',
  ogDescription: 'Ultimate jumps 50% to $29.99 while Essential and Premium expand with PC and cloud access. Read the full breakdown.',
  ogUrl: 'https://steamplayti.me/blog/xbox-game-pass-price-update/',
  twitterTitle: 'Xbox Game Pass Price Hike Explained',
  twitterDescription: 'Essential, Premium, and Ultimate plans change names, pricing, and libraries. Here\'s how it affects your subscription.',
  structuredData: articleStructuredData({
    headline: 'Xbox Game Pass Renames Its Plans and Raises Prices',
    description: 'Microsoft raises Xbox Game Pass prices, renames Core to Essential and Standard to Premium, and folds Ubisoft+ Classics into the library. See what changes and when.',
    canonical: 'https://steamplayti.me/blog/xbox-game-pass-price-update/',
    datePublished: '2025-10-02',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Tools & Blog', path: '/blog/' },
      { name: 'Xbox Game Pass Price Hike and Plan Renames', path: 'https://steamplayti.me/blog/xbox-game-pass-price-update/' },
    ],
  }),
};

export default {
  name: 'XboxGamePassPriceUpdate',
  components: { SeoRelatedTools },
  data() {
    return {
      highlights: HIGHLIGHTS,
      planUpdates: PLAN_UPDATES,
      headlineGames: HEADLINE_GAMES,
      timeline: TIMELINE,
    };
  },
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
};
</script>

<style scoped>
.xbox-game-pass-update {
  background: radial-gradient(circle at top left, rgba(76, 152, 255, 0.16), transparent 65%);
}

.seo-card ul {
  padding-left: 1.1rem;
  margin: 0;
  display: grid;
  gap: 8px;
}

.seo-card ul li {
  color: rgba(255, 255, 255, 0.76);
}

</style>
